import { faBars } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Autocomplete, Chip, TextField, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { arrayMove } from "app/tools/common"
import React from "react"
import SortableList, { SortableItem } from "react-easy-sort"
import { connect } from "react-redux"
import {
  IReportGroupSortBy,
  REPORTS_SORTABLE_ITEM_DEFAULT_CLASSNAME,
  REPORTS_SORTABLE_ITEM_DRAGGED_CLASSNAME
} from "reports/model"
import { setReportsParameters } from "reports/tools/common"
import { getReportForm } from "reports/tools/ui"
import { FlexBox } from "ui/components/FlexBox"
import { MAIN_LAYOUT_ID } from "ui/model"

interface IReportParametersSortByProps {
  isCreation: boolean
}

interface IReportParametersSortByConnectProps {
  groupBys: IReportGroupSortBy[]
  sortBys: IReportGroupSortBy[]
}

const ReportParametersSortByFn = React.memo(
  ({ isCreation, groupBys, sortBys }: IReportParametersSortByProps & IReportParametersSortByConnectProps) => {
    const ref = React.useRef(document.getElementById(MAIN_LAYOUT_ID))
    if (groupBys.length === 0) {
      return (
        <Typography id="reports-sort-by-empty-text" color="textSecondary" fontSize=".9rem">
          {translate("reports.sort.by.empty")}
        </Typography>
      )
    }
    return (
      <FlexBox
        jc="start"
        vertical
        fullWidth
        gap
        sx={{
          "& .list": {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: ".5rem"
          },
          "& .list > *": {
            width: "100%"
          }
        }}>
        <Autocomplete
          multiple
          sx={{ "width": "100%", "& input::placeholder": { fontSize: ".7rem" } }}
          size="small"
          id="reports-parameters-sort-select"
          options={groupBys}
          value={sortBys}
          filterSelectedOptions
          onChange={(_event, newValues) => setReportsParameters(isCreation, { sortBys: newValues })}
          limitTags={2}
          getOptionLabel={sortBy => translate(`report.group.sort.by.${sortBy}`)}
          renderTags={(value, getTagProps) =>
            value.map((sortBy, index) => (
              <Chip
                id={`autocomplete-chip-${sortBy}`}
                label={translate(`report.group.sort.by.${sortBy}`)}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={params => (
            <TextField {...params} label={`${translate("button.add")} / ${translate("button.delete")}`} />
          )}
        />
        {sortBys.length > 1 && (
          <Typography color="textSecondary" fontSize=".85rem" sx={{ width: "100%" }}>
            {translate("reports.sort.help")}
          </Typography>
        )}
        <SortableList
          onSortEnd={(oldIndex: number, newIndex: number) =>
            setReportsParameters(isCreation, { sortBys: arrayMove(sortBys, oldIndex, newIndex) })
          }
          className="list"
          draggedItemClassName={REPORTS_SORTABLE_ITEM_DRAGGED_CLASSNAME}
          customHolderRef={ref}>
          {sortBys.map((sortBy, index) => (
            <SortableItem key={index}>
              <div id={`selected-sort-by-${sortBy}`} className={REPORTS_SORTABLE_ITEM_DEFAULT_CLASSNAME} key={index}>
                <Typography color="textPrimary" fontSize=".85rem">
                  {translate(`report.group.sort.by.${sortBy}`)}
                </Typography>
                <FontAwesomeIcon icon={faBars} />
              </div>
            </SortableItem>
          ))}
        </SortableList>
      </FlexBox>
    )
  }
)

export const ReportParametersSortBy = connect(
  (state: IRootState, ownProps: IReportParametersSortByProps): IReportParametersSortByConnectProps => {
    const form = getReportForm(ownProps.isCreation, state.reports)
    return {
      groupBys: form.parameters.groupBys,
      sortBys: form.parameters.sortBys
    }
  }
)(ReportParametersSortByFn)
